<template>
    <div class="items-list-container">
        <h6 class="list-title">Pilares</h6>
        
        <div class="items-tree">
            <!-- Pilar -->
            <div 
                v-for="pillar in pillars" 
                :key="'pillar-' + pillar.id"
                class="tree-level pillar-level"
            >
                <div 
                    class="tree-item pillar-item"
                    :style="{ borderLeftColor: pillar.color }"
                    @click="togglePillar(pillar.id)"
                >
                    <i 
                        class="fa" 
                        :class="isPillarExpanded(pillar.id) ? 'fa-chevron-down' : 'fa-chevron-right'"
                    ></i>
                    <span class="item-name">{{ pillar.name }}</span>
                </div>

                <!-- Elementos -->
                <div v-if="isPillarExpanded(pillar.id)" class="tree-children">
                    <div 
                        v-for="element in pillar.elements" 
                        :key="'element-' + element.id"
                        class="tree-level element-level"
                    >
                        <div 
                            class="tree-item element-item"
                            :style="{ borderLeftColor: element.color }"
                            @click="toggleElement(element.id)"
                        >
                            <i 
                                class="fa" 
                                :class="isElementExpanded(element.id) ? 'fa-chevron-down' : 'fa-chevron-right'"
                            ></i>
                            <span class="item-name">{{ element.name }}</span>
                        </div>

                        <!-- Items Generales -->
                        <div v-if="isElementExpanded(element.id)" class="tree-children">
                            <div 
                                v-for="generalItem in element.general_items" 
                                :key="'general-' + generalItem.id"
                                class="tree-level general-item-level"
                            >
                                <div 
                                    class="tree-item general-item"
                                    @click="toggleGeneralItem(generalItem.id)"
                                >
                                    <i 
                                        class="fa" 
                                        :class="isGeneralItemExpanded(generalItem.id) ? 'fa-chevron-down' : 'fa-chevron-right'"
                                    ></i>
                                    <span class="item-name">{{ generalItem.name }}</span>
                                </div>

                                <!-- Items evaluables -->
                                <div v-if="isGeneralItemExpanded(generalItem.id)" class="tree-children">
                                    <div 
                                        v-for="item in generalItem.items" 
                                        :key="'item-' + item.id"
                                        class="tree-level item-level"
                                    >
                                        <div 
                                            class="item-card"
                                            :class="{ 
                                                'active': selectedItemId === item.id,
                                                'synced': item.response && item.response.is_synced,
                                                'not-synced': !item.response || !item.response.is_synced
                                            }"
                                            @click="selectItem(item)"
                                        >
                                            <div class="item-card-header">
                                                <span 
                                                    v-if="item.response && item.response.rating"
                                                    class="rating-pill text-left font-weight-semibold"
                                                    :style="{ backgroundColor: getRatingColor(item.response.rating.id) }"
                                                >
                                                    {{ item.response.rating.name }}
                                                </span>
                                                <span class="status-indicator" :class="item.response && item.response.is_synced ? 'status-indicator--synced' : 'status-indicator--pending'">
                                                    <i class="fa" :class="item.response && item.response.is_synced ? 'fa-check' : 'fa-circle-o'"></i>
                                                </span>
                                            </div>
                                            <div class="item-card-body">
                                                <p class="item-question">{{ item.short_name }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <div v-if="pillars.length === 0" class="empty-state">
            <i class="fa fa-filter fa-3x"></i>
            <p>No se encontraron ítems con los filtros aplicados</p>
            <p class="empty-hint">Intenta ajustar o limpiar los filtros</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ItemsList',
    props: {
        pillars: {
            type: Array,
            required: true,
            default: () => []
        },
        selectedItemId: {
            type: Number,
            default: null
        },
        ratingDefinitions: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            expandedPillar: null,
            expandedElement: null,
            expandedGeneralItem: null
        };
    },
    watch: {
        pillars: {
            immediate: true,
            handler(newPillars) {
                // Auto-expandir el primer pilar si hay datos
                if (newPillars.length > 0 && !this.expandedPillar) {
                    this.expandedPillar = newPillars[0].id;
                    
                    if (newPillars[0].elements && newPillars[0].elements.length > 0) {
                        this.expandedElement = newPillars[0].elements[0].id;
                        
                        if (newPillars[0].elements[0].general_items && newPillars[0].elements[0].general_items.length > 0) {
                            this.expandedGeneralItem = newPillars[0].elements[0].general_items[0].id;
                        }
                    }
                }
            }
        }
    },
    methods: {
        togglePillar(pillarId) {
            // Si ya está expandido, colapsar. Si no, expandir y colapsar otros
            if (this.expandedPillar === pillarId) {
                this.expandedPillar = null;
                this.expandedElement = null;
                this.expandedGeneralItem = null;
            } else {
                this.expandedPillar = pillarId;
                this.expandedElement = null;
                this.expandedGeneralItem = null;
            }
        },
        
        toggleElement(elementId) {
            // Si ya está expandido, colapsar. Si no, expandir y colapsar otros
            if (this.expandedElement === elementId) {
                this.expandedElement = null;
                this.expandedGeneralItem = null;
            } else {
                this.expandedElement = elementId;
                this.expandedGeneralItem = null;
            }
        },
        
        toggleGeneralItem(generalItemId) {
            // Si ya está expandido, colapsar. Si no, expandir y colapsar otros
            if (this.expandedGeneralItem === generalItemId) {
                this.expandedGeneralItem = null;
            } else {
                this.expandedGeneralItem = generalItemId;
            }
        },
        
        isPillarExpanded(pillarId) {
            return this.expandedPillar === pillarId;
        },
        
        isElementExpanded(elementId) {
            return this.expandedElement === elementId;
        },
        
        isGeneralItemExpanded(generalItemId) {
            return this.expandedGeneralItem === generalItemId;
        },
        
        selectItem(item) {
            this.$emit('item-selected', item);
        },
        
        getRatingColor(ratingId) {
            const rating = this.ratingDefinitions.find(r => r.id === ratingId);
            return rating && rating.color ? rating.color : '#9CA3AF';
        }
    }
};
</script>

<style scoped>
.items-list-container {
    background: white;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    height: calc(100vh - 280px);
    display: flex;
    flex-direction: column;
}

.list-title {
    font-size: 14px;
    font-weight: 600;
    color: #1F2937;
    padding: 16px;
    border-bottom: 1px solid #E5E7EB;
    margin: 0;
}

.items-tree {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
}

/* Niveles del árbol */
.tree-level {
    margin-bottom: 8px;
}

.pillar-level {
    margin-bottom: 12px;
}

.element-level {
    margin-bottom: 8px;
}

.tree-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.15s ease;
    user-select: none;
}

.tree-item:hover:not(.pillar-item):not(.element-item) {
    background-color: #F9FAFB !important;
}

.tree-item i {
    font-size: 12px;
    color: #6B7280;
    width: 12px;
    flex-shrink: 0;
}

.item-name {
    flex: 1;
    font-size: 14px;
    color: #1F2937;
    line-height: 1.4;
    white-space: normal;
}

.item-text {
    flex: 1;
    padding-right: 130px;
}

.item-short {
    margin: 0 0 4px 0;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6B7280;
}

.item-question {
    margin: 0;
    font-size: 14px;
    line-height: 1.55;
    color: #1F2937;
}

/* Pilar */
.pillar-item {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 0.5px;
    background-color: white !important;
    border-top: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    border-bottom: 1px solid #E5E7EB;
    border-left: 5px solid;
    border-radius: 6px;
    padding: 12px !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.pillar-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

/* Elemento */
.element-item {
    font-weight: 600;
    margin-left: 16px;
    background-color: white !important;
    border-top: 1px solid #E5E7EB;
    border-right: 1px solid #E5E7EB;
    border-bottom: 1px solid #E5E7EB;
    border-left: 4px solid;
    border-radius: 6px;
    padding: 10px 12px !important;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.element-item:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

/* Item General */
.general-item {
    margin-left: 32px;
    font-weight: 500;
    background-color: white;
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    padding: 10px 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.general-item:hover {
    background-color: #F9FAFB !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Item evaluable */
.evaluable-item {
    margin-left: 48px;
    background-color: white;
    border: 1px solid #E5E7EB;
}

.evaluable-item:hover {
    border-color: #10B981;
    background-color: #F0FDF4 !important;
}

.evaluable-item.active {
    background-color: #D1FAE5 !important;
    border-color: #10B981;
    border-width: 2px;
}

.evaluable-item.active .item-name {
    color: #065F46;
    font-weight: 600;
}

.item-card {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    margin-left: 48px;
    background-color: white;
    border: 1px solid #E5E7EB;
    border-radius: 12px;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
    cursor: pointer;
    transition: all 0.15s ease;
}

.item-card:hover {
    border-color: #10B981;
    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.15);
}

.item-card.active {
    border-color: #0f9d74;
    box-shadow: 0 6px 18px rgba(16, 185, 129, 0.18);
}

.item-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-bottom: 0;
}

.rating-pill {
    padding: 2px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: white;
    white-space: normal;
    line-height: 1.2;
    max-width: 200px;
}

.status-indicator {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #E5E7EB;
    color: #9CA3AF;
    background: #F3F4F6;
}

.status-indicator--synced {
    border-color: #10B981;
    background: #ECFDF5;
    color: #059669;
}

.status-indicator--pending {
    border-color: #E5E7EB;
    background: #F9FAFB;
    color: #9CA3AF;
}

.item-card-body {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.item-card.active .item-question {
    color: #065F46;
}

.tree-children {
    margin-top: 4px;
}

/* Empty state */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: #9CA3AF;
    text-align: center;
}

.empty-state i {
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
}

.empty-hint {
    font-size: 12px !important;
    margin-top: 8px !important;
    opacity: 0.7;
}

/* Scrollbar */
.items-tree::-webkit-scrollbar {
    width: 4px;
}

.items-tree::-webkit-scrollbar-track {
    background: #F3F4F6;
}

.items-tree::-webkit-scrollbar-thumb {
    background: #C7CCD5;
    border-radius: 4px;
}

.items-tree::-webkit-scrollbar-thumb:hover {
    background: #AEB4C0;
}
</style>
